import { BaseFilterComponent } from './types';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { GeneOutlierFilterOption } from '../options/types';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { Filter } from '../../types';
import * as React from 'react';
import { useSelectedFilterOptions } from '../../../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import {
  errorFor,
  FormErrorMessage,
} from '@lifeomic/chroma-react/components/_private/forms';
import { useStyles as useTextFieldStyles } from '@lifeomic/chroma-react/components/TextField/TextField';
import { v4 as uuid } from 'uuid';

const NO_SELECTION = '<no selection>';

const messages = defineMessages({
  sourceLabel: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.sourceLabel',
    defaultMessage: 'Compare against source',
  },
  project: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.project',
    defaultMessage: 'Project Samples',
  },
  tcgaTumor: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.tcgaTumor',
    defaultMessage: 'TCGA Tumor Samples',
  },
  tcgaNormal: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.tcgaNormal',
    defaultMessage: 'TCGA Normal Samples',
  },
  gtexBroad: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.gtexBroad',
    defaultMessage: 'GTEx Normal Broad Tissues',
  },
  gtexSpecific: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.gtexSpecific',
    defaultMessage: 'GTEx Normal Specific Tissues',
  },
  treeHouse: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.treeHouse',
    defaultMessage: 'TreeHouse Tumor Samples',
  },
  overAvg: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.overAvg',
    defaultMessage: 'Above the mean',
  },
  underAvg: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.underAvg',
    defaultMessage: 'Below the mean',
  },
  overMedian: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.overMedian',
    defaultMessage: 'Above the median',
  },
  underMedian: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.underMedian',
    defaultMessage: 'Below the median',
  },
  addFilter: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.addFilter',
    defaultMessage: 'Add Filter',
  },
  selectSource: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.selectSource',
    defaultMessage: 'Select a source reference option',
  },
  selectAboveOrBelow: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.selectAboveOrBelow',
    defaultMessage: 'Select above or below options',
  },
  geneRequired: {
    id: 'omicsExplorer.filterResultsManager.components.geneOutlier.geneRequired',
    defaultMessage: 'Gene or gene set are required',
  },
});

interface GeneOutlierProps
  extends GeneOutlierFilterOption,
    BaseFilterComponent {}

interface OutlierState {
  stdDev?: string;
  stat?: string;
  source?: string;
  sourceReference?: string;
}

const getSourceLabel = (source: string) => {
  if (source.includes('tcga')) {
    return 'TCGA';
  } else if (source.includes('gtex')) {
    return 'GTEx';
  } else if (source.includes('tree')) {
    return 'TreeHouse';
  }
  return '';
};

const getSourceReferenceLabel = (sourceReference: string) => {
  const abbreviation = sourceReference.match(/\[(.*?)\]/)?.[1];
  return abbreviation || sourceReference.replace(/\s*\(.*?\)\s*/g, ''); // Remove sample count
};

export const GeneOutlier: React.FC<GeneOutlierProps> = ({
  injectedProps: { geneOutlierOption, sourceReferenceOption, searchKey },
  onFilterAdd,
  origin,
}) => {
  const { formatMessage } = useIntl();
  const textFieldClasses = useTextFieldStyles({});
  const errorMessageId = uuid();

  const [selectedFilterOptions] = useSelectedFilterOptions();
  const hasGeneFilter = selectedFilterOptions.some(
    option => option.searchKey === 'gene',
  );

  const [{ stdDev, stat, source, sourceReference }, setOutlierValue] =
    React.useState<OutlierState>({
      stdDev: '',
      stat: 'over_avg',
      source: 'project',
      sourceReference: NO_SELECTION,
    });
  const [sourceReferenceOptions, setSourceReferenceOptions] = React.useState(
    sourceReferenceOption.projectOptions,
  );

  const isSubmitDisabled =
    !stdDev ||
    stdDev?.trim?.()?.length === 0 ||
    (!hasGeneFilter && !!stdDev?.trim?.()?.length);

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOutlierValue({
      stat,
      stdDev: e.target.value,
      source,
      sourceReference,
    });
  };

  const handleSelectChange = (value: string) => {
    setOutlierValue({
      stdDev,
      stat: value,
      source,
      sourceReference,
    });
  };

  const handleSourceChange = (value: string) => {
    setOutlierValue({
      stdDev,
      stat,
      source: value,
      sourceReference: NO_SELECTION,
    });
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    setSourceReferenceOptions(sourceReferenceOption[`${value}Options`]);
  };

  const handleSourceReferenceChange = (value: string) => {
    setOutlierValue({
      stdDev,
      stat,
      source,
      sourceReference: value,
    });
  };

  const handleSubmitClick = () => {
    if (!stdDev || stdDev?.trim?.()?.length === 0) {
      return;
    }

    if (onFilterAdd) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const [level, statValue] = stat.split('_');

      const option: Filter = {
        id: geneOutlierOption.id,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        origin,
        searchKey,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        friendlyName: formatMessage(geneOutlierOption[stat], { stdDev }),
        searchValue: `${stdDev}:${level}:${statValue}:${source}${
          sourceReference === NO_SELECTION ? '' : ':' + sourceReference
        }`,
        tooltipTitle: formatMessage(geneOutlierOption.tooltipTitle, {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          outlier: formatMessage(geneOutlierOption[stat], {
            stdDev,
          }),
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          source: getSourceLabel(source),
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          sourceReference: getSourceReferenceLabel(sourceReference),
        }),
      };

      onFilterAdd(option);
    }

    setOutlierValue({
      stdDev: '',
      stat: 'over_avg',
      source: 'project',
      sourceReference: NO_SELECTION,
    });
  };

  return (
    <FormBox id={errorMessageId}>
      <Select
        label={formatMessage(messages.sourceLabel)}
        value={source}
        onChange={handleSourceChange}
        fullWidth
      >
        <SelectOption title={formatMessage(messages.project)} value="project" />
        <SelectOption
          title={formatMessage(messages.tcgaTumor)}
          value="tcga_tumor"
        />
        <SelectOption
          title={formatMessage(messages.tcgaNormal)}
          value="tcga_normal"
        />
        <SelectOption
          title={formatMessage(messages.gtexBroad)}
          value="gtex_broad"
        />
        <SelectOption
          title={formatMessage(messages.gtexSpecific)}
          value="gtex_specific"
        />
        <SelectOption
          title={formatMessage(messages.treeHouse)}
          value="tree_house"
        />
      </Select>
      <Select
        value={sourceReference}
        onChange={handleSourceReferenceChange}
        disabled={source === 'project'}
        label=""
        aria-label={formatMessage(messages.selectSource)}
        popoverAriaLabel={formatMessage(messages.selectSource)}
        fullWidth
      >
        {sourceReferenceOptions.map(o => (
          <SelectOption
            key={o.intlLabel.id}
            title={
              (
                <FormattedMessage
                  id={o.intlLabel.id}
                  defaultMessage={o.intlLabel.defaultMessage}
                />
              ) as any
            }
            value={o.searchValue}
          />
        ))}
      </Select>
      <TextField
        type="number"
        label={formatMessage(geneOutlierOption.helperTextLabel)}
        min={0}
        value={stdDev}
        onChange={handleTextFieldChange}
        fullWidth
      />
      <Select
        value={stat}
        onChange={handleSelectChange}
        label=""
        aria-label={formatMessage(messages.selectAboveOrBelow)}
        popoverAriaLabel={formatMessage(messages.selectAboveOrBelow)}
        fullWidth
      >
        <SelectOption
          title={formatMessage(messages.overAvg)}
          value="over_avg"
        />
        <SelectOption
          title={formatMessage(messages.underAvg)}
          value="under_avg"
        />
        <SelectOption
          title={formatMessage(messages.overMedian)}
          value="over_median"
        />
        <SelectOption
          title={formatMessage(messages.underMedian)}
          value="under_median"
        />
      </Select>

      {!hasGeneFilter && !!stdDev?.trim?.()?.length && (
        <FormErrorMessage
          className={textFieldClasses.trailerMessage}
          describedById={errorFor(errorMessageId)}
          rootElementId={errorMessageId}
        >
          {formatMessage(messages.geneRequired)}
        </FormErrorMessage>
      )}

      <Button
        variant="contained"
        onClick={handleSubmitClick}
        disabled={isSubmitDisabled}
        fullWidth
      >
        {formatMessage(messages.addFilter)}
      </Button>
    </FormBox>
  );
};
